/**
 * Optional merchant filter for the Ledger — a custom type-ahead over the
 * super-admin tenants list (business names). Picking a suggestion filters by
 * the exact tenant id; typing without picking falls back to the ledger's own
 * id / business-name substring filter (debounced by the caller).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { RubyGemMark } from '../../../components/RubyGemMark';
import { listLedgerMerchants, type LedgerMerchant } from '../../../services/aiControl/ledger';

export interface MerchantValue {
  /** What the input shows. */
  text: string;
  /** Set when a suggestion was picked — the exact tenant id. */
  tenantId?: string;
}

const MAX_SUGGESTIONS = 8;

const LedgerMerchantSearch: React.FC<{ value: MerchantValue; onChange: (v: MerchantValue) => void }> = ({ value, onChange }) => {
  const [merchants, setMerchants] = useState<LedgerMerchant[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const loadStarted = useRef(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const ensureLoaded = () => {
    if (loadStarted.current) return;
    loadStarted.current = true;
    setLoading(true);
    listLedgerMerchants()
      .then((m) => setMerchants(m))
      .catch(() => setMerchants([])) // no directory → the typed text still filters by name substring
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const suggestions = useMemo(() => {
    const q = value.text.trim().toLowerCase();
    if (!q || !merchants || value.tenantId) return [];
    return merchants
      .filter((m) => m.name.toLowerCase().includes(q) || m.tenantId.toLowerCase().includes(q))
      .sort((a, b) => Number(!a.name.toLowerCase().startsWith(q)) - Number(!b.name.toLowerCase().startsWith(q)) || a.name.localeCompare(b.name))
      .slice(0, MAX_SUGGESTIONS);
  }, [merchants, value.text, value.tenantId]);

  const pick = (m: LedgerMerchant) => { onChange({ text: m.name, tenantId: m.tenantId }); setOpen(false); };

  return (
    <div ref={boxRef} className="relative w-full sm:w-[260px]">
      <div className="flex items-center min-h-[44px] px-3 gap-2 rounded-lg border border-stone-200 bg-white focus-within:border-[#DC143C] focus-within:ring-2 focus-within:ring-rose-100">
        <Search size={14} className="text-stone-400 shrink-0" />
        <input
          type="text"
          role="combobox"
          aria-expanded={open && suggestions.length > 0}
          aria-autocomplete="list"
          aria-label="Filter by merchant"
          placeholder="Merchant (optional)"
          value={value.text}
          onFocus={() => { ensureLoaded(); setOpen(true); }}
          onChange={(e) => { ensureLoaded(); setOpen(true); setActive(0); onChange({ text: e.target.value }); }}
          onKeyDown={(e) => {
            if (!suggestions.length) return;
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, suggestions.length - 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
            if (e.key === 'Enter') { e.preventDefault(); pick(suggestions[active]); }
            if (e.key === 'Escape') setOpen(false);
          }}
          className="flex-1 min-w-0 bg-transparent outline-none text-[13px] text-stone-900 placeholder:text-stone-400"
        />
        {loading && <RubyGemMark size={14} drawing color="#DC143C" title="Loading merchants" />}
        {value.text && (
          <button
            type="button"
            aria-label="Clear merchant filter"
            onClick={() => onChange({ text: '' })}
            className="-mr-2 min-h-[44px] min-w-[36px] inline-flex items-center justify-center text-stone-400 hover:text-stone-700"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <ul role="listbox" aria-label="Merchant suggestions" className="absolute z-30 mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg py-1 max-h-72 overflow-auto">
          {suggestions.map((m, i) => (
            <li
              key={m.tenantId}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); pick(m); }}
              onMouseEnter={() => setActive(i)}
              className={`px-3 min-h-[44px] flex flex-col justify-center cursor-pointer ${i === active ? 'bg-rose-50' : 'bg-white'}`}
            >
              <span className="text-[13px] text-stone-900 truncate">{m.name}</span>
              <span className="text-[11px] font-mono text-stone-400 truncate">{m.tenantId}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LedgerMerchantSearch;
